import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import AdminLayout from '../../components/admin/AdminLayout';
import AdminCard from '../../components/admin/AdminCard';
import adminAxios from '../../services/adminAxios';
import { formatDate } from '../../utils/dateHelper';
import config from '../../config/config';
import '../../assets/css/admin/moments.css';

const MomentsPage = () => {
  const [moments, setMoments] = useState([]);
  const [filteredMoments, setFilteredMoments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterEvent, setFilterEvent] = useState('');
  const [events, setEvents] = useState([]);
  const momentsPerPage = 12;

  const fetchMoments = useCallback(async () => {
    if (!loading) setLoading(true);
    try {
      // Fetch all moments without pagination first to get complete data
      const response = await adminAxios.get(`/api/admin/moments?include_details=true`);
      
      if (response.data.success) {
        setMoments(response.data.moments);
        
        // Extract unique events for filter dropdown
        const uniqueEvents = [...new Map(
          response.data.moments
            .filter(moment => moment.event_title)
            .map(moment => [moment.event_id, { id: moment.event_id, title: moment.event_title }])
        ).values()];
        setEvents(uniqueEvents);
        
        setError(null);
      } else {
        setError('Failed to load moments');
      }
    } catch (error) {
      console.error('Error fetching moments:', error);
      setError('Failed to load moments. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  // Filter and search moments
  useEffect(() => {
    let filtered = [...moments];

    // Apply search filter
    if (searchTerm.trim()) {
      filtered = filtered.filter(moment => 
        moment.event_title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        moment.filename?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply event filter
    if (filterEvent) {
      filtered = filtered.filter(moment => 
        moment.event_id?.toString() === filterEvent.toString()
      );
    }

    setFilteredMoments(filtered);
    
    // Calculate pagination for filtered results
    const pages = Math.ceil(filtered.length / momentsPerPage);
    setTotalPages(pages);
    
    // Reset to first page when filters change
    if (currentPage > pages && pages > 0) {
      setCurrentPage(1);
    }
  }, [moments, searchTerm, filterEvent, currentPage]);

  // Get current page moments
  const getCurrentPageMoments = () => {
    const startIndex = (currentPage - 1) * momentsPerPage;
    const endIndex = startIndex + momentsPerPage;
    return filteredMoments.slice(startIndex, endIndex);
  };

  useEffect(() => {
    fetchMoments();
  }, [fetchMoments]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleEventFilterChange = (e) => {
    setFilterEvent(e.target.value);
    setCurrentPage(1);
  };

  const currentPageMoments = getCurrentPageMoments();

  return (
    <AdminLayout>
      <div className="container-fluid px-4">
        <h1 className="mt-4">Moments Management</h1>
        <ol className="breadcrumb mb-4">
          <li className="breadcrumb-item"><Link to="/admin/dashboard">Dashboard</Link></li>
          <li className="breadcrumb-item active">Moments</li>
        </ol>
        
        <AdminCard 
          title={`All Moments (${filteredMoments.length} ${filterEvent ? 'for selected event' : ''} ${searchTerm ? 'matching' : 'total'})`}
          headerActions={
            <div className="d-flex gap-2">
              <input 
                type="text"
                className="form-control form-control-sm"
                placeholder="Search moments..."
                value={searchTerm}
                onChange={handleSearchChange}
                style={{ minWidth: '200px' }}
              />
              <select 
                className="form-select form-select-sm"
                value={filterEvent}
                onChange={handleEventFilterChange}
                style={{ minWidth: '180px' }}
              >
                <option value="">All Events</option>
                {events.map(event => (
                  <option key={event.id} value={event.id}>
                    {event.title}
                  </option>
                ))}
              </select>
            </div>
          }
          noPadding
        >
          {loading ? (
            <div className="text-center p-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : error ? (
            <div className="alert alert-danger">{error}</div>
          ) : (
            <>
              {currentPageMoments.length > 0 ? (
                <div className="row p-3">
                  {currentPageMoments.map(moment => (
                    <div key={moment.id} className="col-md-3 col-sm-4 col-6 mb-4">
                      <div className="moment-card">
                        <div className="moment-image-container">
                          <img 
                            src={config.getFullUrl(moment.url)} 
                            alt={moment.filename}
                            className="moment-image"
                            onError={(e) => {
                              e.target.src = 'https://placehold.co/300x200?text=Image+Not+Found';
                            }}
                          />
                        </div>
                        <div className="moment-info">
                          <h6 className="moment-title">{moment.event_title}</h6>
                          <p className="moment-filename">{moment.filename}</p>
                          <p className="moment-date">{formatDate(moment.uploaded_at)}</p>
                          <Link to={`/admin/events/${moment.event_id}`} className="btn btn-sm btn-outline-primary">
                            <i className="fa fa-calendar me-1"></i> View Event
                          </Link>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-5">
                  <div className="empty-state-inline">
                    <i className="fa fa-camera me-2"></i>
                    <span>
                      {searchTerm || filterEvent 
                        ? 'No moments match your search criteria' 
                        : 'No moments found'
                      }
                    </span>
                  </div>
                </div>
              )}

              {totalPages > 1 && (
                <div className="d-flex justify-content-between align-items-center mt-4 px-3 pb-3">
                  <div className="text-muted small">
                    Showing {((currentPage - 1) * momentsPerPage) + 1} to {Math.min(currentPage * momentsPerPage, filteredMoments.length)} of {filteredMoments.length} moments
                  </div>
                  <nav>
                    {/* ...existing pagination code... */}
                  </nav>
                </div>
              )}
            </>
          )}
        </AdminCard>
      </div>
    </AdminLayout>
  );
};

export default MomentsPage;
